/**
 * Code Utility Functions
 *
 * Utility functions for code artefact processing, language detection,
 * and styling configuration.
 */

import type { Artefact } from "@/types/artefacts";

import { ARTEFACT_CONFIG } from "@/config/artefacts";

/**
 * Language information interface
 */
export interface LanguageInfo {
  name: string;
  color: string;
  bg: string;
  icon?: string;
}

/**
 * Code display configuration
 */
export interface CodeDisplayConfig {
  language: LanguageInfo;
  lineCount?: number;
  showLineNumbers?: boolean;
  wrapLines?: boolean;
}

/**
 * Get language information from language string
 * Centralizes language detection and styling logic
 */
export const getLanguageInfo = (language?: string): LanguageInfo => {
  const normalizedLang = language?.toLowerCase() || "";

  return (
    ARTEFACT_CONFIG.languages[normalizedLang as keyof typeof ARTEFACT_CONFIG.languages] ||
    ARTEFACT_CONFIG.languages.default
  );
};

/**
 * Get complete code display configuration for an artefact
 * Single source of truth for code display settings
 */
export const getCodeDisplayConfig = (artefact: Artefact): CodeDisplayConfig => {
  const languageInfo = getLanguageInfo(artefact.language);

  return {
    language: languageInfo,
    lineCount: artefact.metadata?.lineCount,
    showLineNumbers: false, // Could be made configurable
    wrapLines: false, // Could be made configurable
  };
};

/**
 * Format line count display text
 * Note: Keeping "1 lines" for backward compatibility with tests
 */
export const formatLineCount = (lineCount?: number): string => {
  if (!lineCount) return "";

  return `${lineCount} lines`;
};

/**
 * Get CSS classes for code container
 */
export const getCodeContainerClasses = (_config: CodeDisplayConfig): string => {
  return `bg-default-100 dark:bg-default-100/50 rounded-lg p-4 border border-default-200 dark:border-default-700`;
};

/**
 * Get CSS classes for language label
 */
export const getLanguageLabelClasses = (_config: CodeDisplayConfig): string => {
  return `text-xs text-default-600 dark:text-default-400 mb-2 font-medium`;
};

/**
 * Get CSS classes for code block
 */
export const getCodeBlockClasses = (config: CodeDisplayConfig): string => {
  const baseClasses =
    "text-sm font-mono text-default-800 dark:text-white whitespace-pre overflow-x-auto p-4 leading-relaxed";

  if (config.wrapLines) {
    return baseClasses.replace("whitespace-pre", "whitespace-pre-wrap");
  }

  return baseClasses;
};

/**
 * Build display metadata string for code artefact
 */
export const buildCodeMetadata = (config: CodeDisplayConfig): string => {
  const parts = [config.language.name];

  const lineCountText = formatLineCount(config.lineCount);

  if (lineCountText) {
    parts.push(lineCountText);
  }

  return parts.join(" • ");
};
